import React from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1528207620916-db0d5aab17cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxpbmRvbmVzaWFuJTIwZm9vZCUyMGJhbmFuYSUyMGxlYWZ8ZW58MHx8fHwxNzgxNDM2NzY1fDA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const { t } = useLang();

  return (
    <section
      id="top"
      className="relative min-h-[100svh] w-full overflow-hidden bg-brand-ink"
      data-testid="hero-section"
    >
      <motion.div
        initial={{ scale: 1.1, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-0"
      >
        <img
          src={HERO_IMG}
          alt="Bandeng Primaroso"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/40 to-brand-ink/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-ink/70 via-transparent to-transparent" />
      </motion.div>

      <div className="relative z-10 min-h-[100svh] flex flex-col">
        <div className="flex-1" />
        <div className="max-w-[1600px] w-full mx-auto px-6 md:px-12 pb-20 md:pb-24">
          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 lg:col-span-9">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="flex items-center gap-4 mb-8"
              >
                <span className="block w-12 h-px bg-brand-yellow" />
                <span className="font-sans text-[11px] md:text-xs tracking-[0.3em] uppercase text-brand-yellow">
                  {t.hero.kicker}
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="font-serif text-brand-cream tracking-tight leading-[0.95]"
                data-testid="hero-headline"
              >
                <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
                  {t.hero.mainPrimary}
                </span>
                <span className="block italic text-5xl sm:text-6xl md:text-7xl lg:text-8xl mt-2 text-brand-yellow/95">
                  {t.hero.mainSecondary}
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1, ease: [0.16, 1, 0.3, 1] }}
                className="mt-8 font-sans text-sm md:text-base tracking-wide text-brand-cream/70 max-w-xl"
              >
                {t.hero.sub}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
                className="mt-10 flex flex-wrap gap-3"
              >
                <a
                  href="#shop"
                  className="inline-flex items-center gap-3 px-6 py-3 bg-brand-red text-brand-cream font-sans text-sm tracking-wider hover:bg-brand-red-dark transition-colors group"
                  data-testid="hero-cta"
                >
                  {t.hero.cta}
                  <ArrowDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
                </a>
                <a
                  href="#wholesale"
                  className="inline-flex items-center gap-3 px-6 py-3 border border-brand-cream/40 text-brand-cream font-sans text-sm tracking-wider hover:border-brand-yellow hover:text-brand-yellow transition-colors"
                  data-testid="hero-cta-wholesale"
                >
                  {t.hero.cta2}
                </a>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 1.4 }}
              className="hidden lg:flex col-span-3 justify-end items-end"
            >
              <div className="flex flex-col items-center gap-3 text-brand-cream/60">
                <span className="vertical-text font-sans text-xs tracking-[0.4em] uppercase">
                  {t.hero.scroll}
                </span>
                <motion.span
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="block w-px h-16 bg-brand-cream/40"
                />
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="absolute top-1/2 right-6 md:right-12 -translate-y-1/2 hidden md:flex flex-col items-end gap-2"
        >
          <span className="outline-number text-[10rem] md:text-[14rem] leading-none opacity-30 select-none" style={{ WebkitTextStrokeColor: '#F9F6F0' }}>
            01
          </span>
        </motion.div>
      </div>
    </section>
  );
};
